import React, { useEffect, useState, useRef, useCallback, createContext } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import Navbar from "components/navbar";
import Sidebar from "components/sidebar";
import Footer from "components/footer/Footer";
import routes from "routes.js";
import { supabase } from "lib/supabaseClient";
import { insertLog } from "services/logService";

export const UserContext = createContext();

// ─── Super admins who can delete logs ──────────────────────
const SUPER_ADMIN_IDS = [
  "6f226d92-8217-415b-991a-69da541ed483", // Abdulaziz Al-Sultan
  "dea0edd5-c02f-4203-bfe2-c57f9b10d965", // Muath Al-Aqeel
  "00c3e1ce-6328-4ac2-864e-f636884b1234", // حساب للتجارب
];

const IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

export default function Admin(props) {
  const { ...rest } = props;
  const location  = useLocation();
  const navigate  = useNavigate();
  const mainPanel = useRef(null);
  const idleTimer = useRef(null);
  const warningTimer = useRef(null);

  const [open,             setOpen]             = useState(true);
  const [currentRoute,     setCurrentRoute]     = useState("Main Dashboard");
  const [showIdleWarning,  setShowIdleWarning]  = useState(false);
  const [idleCountdown,    setIdleCountdown]    = useState(60);
  const countdownRef = useRef(null);

  const [userProfile, setUserProfile] = useState(() => {
    try {
      const s = sessionStorage.getItem("core_admin_user_profile");
      if (s) return JSON.parse(s);
      const l = localStorage.getItem("core_admin_user_profile");
      return l ? JSON.parse(l) : null;
    } catch { return null; }
  });
  const [isUserDataLoading, setIsUserDataLoading] = useState(() => !userProfile);

  // ── Fetch global user data ──
  const fetchGlobalUserData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase.from("employees").select(
          "full_name, job_title, avatar_url, id, employee_number, national_id, phone_number, contract_start, contract_end, annual_leave_balance, annual_leave_used, sick_leave_count, emergency_leave_count, is_on_leave, role, permissions"
        ).eq("id", user.id).single();

        const enriched = profile ? {
          ...profile,
          isSuperAdmin: SUPER_ADMIN_IDS.includes(user.id),
        } : null;

        setUserProfile(enriched);
        try {
          sessionStorage.setItem("core_admin_user_profile", JSON.stringify(enriched));
          localStorage.setItem("core_admin_user_profile", JSON.stringify(enriched));
          const accessPayload = {
            isAdmin: profile?.role === "admin" || user.id === "6f226d92-8217-415b-991a-69da541ed483",
            isSuperAdmin: SUPER_ADMIN_IDS.includes(user.id),
            canAccessStaff: profile?.permissions?.can_access_staff === true,
            actor: {
              id: user.id,
              name: profile?.full_name || user?.email || "Admin",
              number: profile?.employee_number || null,
              role: profile?.role || "",
            },
          };
          sessionStorage.setItem("core_staff_access_cache", JSON.stringify(accessPayload));
          localStorage.setItem("core_staff_access_cache", JSON.stringify(accessPayload));
        } catch {}
      }
    } catch (err) { console.error("Global fetch error:", err); }
    finally { setIsUserDataLoading(false); }
  };

  // ── Auto logout after 15min idle ──
  const doLogout = useCallback(async () => {
    clearTimeout(idleTimer.current);
    clearTimeout(warningTimer.current);
    clearInterval(countdownRef.current);
    setShowIdleWarning(false);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: p } = await supabase.from("employees")
          .select("full_name, employee_number, role").eq("id", user.id).single();
        await insertLog({
          timestamp:    new Date().toISOString(),
          actor_name:   p?.full_name || user.email,
          actor_number: p?.employee_number || "—",
          actor_role:   p?.role || "—",
          action:       "AUTO_LOGOUT",
          details:      "Session expired after 15 minutes of inactivity",
        });
      }
    } catch {}

    localStorage.clear();
    sessionStorage.clear();
    await supabase.auth.signOut();
    navigate("/auth/sign-in");
  }, [navigate]);

  const resetIdleTimer = useCallback(() => {
    clearTimeout(idleTimer.current);
    clearTimeout(warningTimer.current);
    clearInterval(countdownRef.current);
    setShowIdleWarning(false);

    // Show warning 60s before logout
    warningTimer.current = setTimeout(() => {
      setShowIdleWarning(true);
      setIdleCountdown(60);
      let count = 60;
      countdownRef.current = setInterval(() => {
        count -= 1;
        setIdleCountdown(count);
        if (count <= 0) {
          clearInterval(countdownRef.current);
          doLogout();
        }
      }, 1000);
    }, IDLE_TIMEOUT_MS - 60000);

    idleTimer.current = setTimeout(doLogout, IDLE_TIMEOUT_MS);
  }, [doLogout]);

  // Listen for user activity
  useEffect(() => {
    const events = ["mousedown", "mousemove", "keydown", "scroll", "touchstart", "click"];
    const handler = () => resetIdleTimer();
    events.forEach(e => window.addEventListener(e, handler, { passive: true }));
    resetIdleTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, handler));
      clearTimeout(idleTimer.current);
      clearTimeout(warningTimer.current);
      clearInterval(countdownRef.current);
    };
  }, [resetIdleTimer]);

  // ── Scroll on route change ──
  useEffect(() => {
    window.scrollTo(0, 0);
    if (mainPanel.current) mainPanel.current.scrollTo(0, 0);
  }, [location.pathname]);

  // ── Auto Return from Leave ──
  const checkExpiredLeaves = async () => {
    try {
      const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD
      const { data: expiredEmployees, error } = await supabase
        .from("employees")
        .select("id, full_name, employee_number, last_leave_end_date")
        .eq("is_on_leave", true)
        .lte("last_leave_end_date", today);

      if (error) throw error;

      if (expiredEmployees && expiredEmployees.length > 0) {
        for (const emp of expiredEmployees) {
          // Reset status
          await supabase.from("employees").update({
            is_on_leave: false,
            status: "Active",
            last_leave_end_date: null
          }).eq("id", emp.id);

          // Log
          await insertLog({
            action: "AUTO_RETURN_FROM_LEAVE",
            actor_name: "System",
            actor_role: "system",
            target_id: emp.id,
            target_name: emp.full_name,
            target_number: emp.employee_number,
            details: `Auto-returned from leave. Return Date was: ${emp.last_leave_end_date}`,
            timestamp: new Date().toISOString()
          });
        }
        console.log(`[AutoReturn] Processed ${expiredEmployees.length} employees.`);
      }
    } catch (err) {
      console.error("[AutoReturn] Error:", err);
    }
  };

  useEffect(() => {
    fetchGlobalUserData();
    checkExpiredLeaves(); // Run on mount
    const handleResize = () => window.innerWidth < 1200 ? setOpen(false) : setOpen(true);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => { getActiveRoute(routes); }, [location.pathname]);

  const getActiveRoute = (routes) => {
    for (let i = 0; i < routes.length; i++) {
      if (window.location.href.indexOf(routes[i].layout + routes[i].path) !== -1) {
        setCurrentRoute(routes[i].name);
      }
    }
  };

  const getRoutes = (routes) => routes.map((prop, key) => {
    if (prop.layout === "/admin") {
      return <Route path={`/${prop.path}`} element={prop.component} key={key} />;
    }
    return null;
  });

  return (
    <UserContext.Provider value={{ userProfile, isUserDataLoading, refreshUserData: fetchGlobalUserData, SUPER_ADMIN_IDS }}>

      {/* ── Layout CSS: hard-locked 300px sidebar, zero transitions on desktop ── */}
      <style>{`
        /* BASE: sidebar is always fixed, 300px, left:0 */
        .sidebar-fixed {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          height: 100% !important;
          z-index: 110 !important;
          display: flex !important;
          flex-direction: column !important;
          pointer-events: auto !important;
          overflow: hidden !important;
        }

        /* DESKTOP (≥1200px): always visible, zero animation, zero transition */
        @media (min-width: 1200px) {
          .sidebar-fixed {
            width: 300px !important;
            min-width: 300px !important;
            max-width: 300px !important;
            transform: translateZ(0) !important;
            transition: none !important;
            visibility: visible !important;
          }
          .main-content-fixed {
            margin-left: 300px !important;
            transition: none !important;
            width: calc(100% - 300px) !important;
          }
        }

        /* MOBILE (<1200px): slide via transform, no margin shift on content */
        @media (max-width: 1199px) {
          .sidebar-fixed {
            width: 300px !important;
            min-width: 300px !important;
            max-width: 300px !important;
          }
          .sidebar-fixed[data-open="false"] {
            transform: translateX(-100%) !important;
            transition: transform 0.2s ease !important;
          }
          .sidebar-fixed[data-open="true"] {
            transform: translateX(0) !important;
            transition: transform 0.2s ease !important;
          }
          .main-content-fixed {
            margin-left: 0 !important;
            width: 100% !important;
            transition: none !important;
          }
        }
      `}</style>

      <div
        className="relative flex h-full w-full overflow-hidden bg-core-bg text-core-charcoal selection:bg-core-accent/30"
        style={{ fontFamily: "'Kufam', sans-serif" }}
      >
        {/* Background */}
        <div className="pointer-events-none fixed inset-0 z-0 bg-core-bg" />

        {/* ── IDLE WARNING MODAL ── */}
        {showIdleWarning && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-900/50 backdrop-blur-sm">
            <div className="w-full max-w-sm rounded-[32px] border border-orange-200 bg-white p-10 shadow-2xl text-center">
              <div className="mb-4 text-5xl">⏳</div>
              <h3 className="mb-2 text-xl font-light uppercase text-core-charcoal">Session Expiring</h3>
              <p className="mb-6 text-sm text-gray-500">
                You will be signed out due to inactivity in
              </p>
              <div className="mb-8 text-6xl font-extralight text-orange-400 tabular-nums">
                {idleCountdown}s
              </div>
              <button
                onClick={() => { resetIdleTimer(); }}
                className="w-full rounded-2xl bg-core-accent py-4 text-xs uppercase tracking-widest text-white hover:brightness-110 active:scale-95 transition-all"
              >
                Stay Signed In
              </button>
            </div>
          </div>
        )}

        {/* Sidebar: width + visibility locked by .sidebar-fixed CSS */}
        <Sidebar open={open} onClose={() => setOpen(false)} />

        {/* Main panel: margin locked by .main-content-fixed CSS — NO transition here */}
        <div
          ref={mainPanel}
          className="main-content-fixed relative z-10 h-full overflow-y-auto overflow-x-hidden"
        >
          <main className="mx-[12px] h-full flex-none md:pr-2">
            <div className="h-full">
              <Navbar
                onOpenSidenav={() => setOpen(true)}
                brandText={currentRoute}
                secondary={false}
                userProfile={userProfile}
                isUserDataLoading={isUserDataLoading}
                {...rest}
              />
              <div className="mx-auto mb-auto h-full min-h-[84vh] p-2 pt-5 md:pr-2">
                <Routes>
                  {getRoutes(routes)}
                  <Route path="/" element={<Navigate to="/admin/default" replace />} />
                </Routes>
              </div>
              <div className="p-3"><Footer /></div>
            </div>
          </main>
        </div>
      </div>
    </UserContext.Provider>
  );
}
